/**
 * engine/positions.ts — where the listener stopped in each long track, and whether to go on there.
 *
 * Task 49. `spec.md` «Микс и дополнения плеера», stories 138–141: a track of
 * {@link LONG_TRACK_S} seconds or more — an hour's mix — goes on after a break from where the
 * listener stopped, like a podcast; on another phone too; and a track heard almost to its end
 * starts from the beginning. The places live on the server (`apps/api/src/routes/positions.ts`);
 * this module keeps the ones the app knows, so a tap can use one at once: a `play()` has to
 * happen inside the tap, and there is no time to ask the server first.
 *
 * ## When it is read and written
 *
 * - **Read at launch**, all of the listener's places (the shell, beside the hearts), and **again
 *   for one track whenever it starts from its beginning**: the start has already used what was
 *   known, and if another phone moved the place since — or the tap came before the launch's
 *   answer — the player goes there when the answer comes (`engine/player.ts`, «A long track goes
 *   on where it stopped»). The review of task 49 found that without this the old second was not
 *   only heard but, ten seconds later, saved over the newer one.
 * - **Written by the queue's own save** (`engine/sync.ts`), every ten seconds of play and on a
 *   pause, only for a long track, only once it has played in this session, and only when the
 *   second moved.
 *
 * ## Whether to go on from a place
 *
 * {@link resumePoint}: a long track, a place more than {@link RESUME_EDGE_S} seconds in, and more
 * than that left to play. A place in the first half-minute is a start; one in the last half-minute
 * is a track heard to its end, and the next listen starts it over.
 */

import { fetchPositions, savePosition } from '../lib/api';
import { useSession } from '../store/session';

/** Twenty minutes: a mix, not a song. */
export const LONG_TRACK_S = 20 * 60;

/** A place this close to either end is no place to go on from. */
export const RESUME_EDGE_S = 30;

/** The second to start `durationS` at, given where it stopped; 0 — from the start. */
export function resumePoint(durationS: number, placeS: number | undefined): number {
  if (durationS < LONG_TRACK_S || placeS === undefined) return 0;
  if (placeS <= RESUME_EDGE_S || durationS - placeS <= RESUME_EDGE_S) return 0;
  return Math.floor(placeS);
}

/** Whether a track's place is kept at all. */
export function isLongTrack(durationS: number): boolean {
  return durationS >= LONG_TRACK_S;
}

/** Track id → second, as far as this page knows. */
const places = new Map<number, number>();

export function placeOf(trackId: number): number | undefined {
  return places.get(trackId);
}

/** Forgets everything — the web suite's seam between cases. */
export function forgetPlaces(): void {
  places.clear();
}

function token(): string | undefined {
  return useSession.getState().session?.token;
}

function adopt(answer: Readonly<Record<string, number>>): void {
  for (const [id, positionS] of Object.entries(answer)) {
    const trackId = Number(id);
    if (Number.isInteger(trackId) && Number.isFinite(positionS)) places.set(trackId, positionS);
  }
}

/** All of the listener's places, at launch. A failure leaves the app with none: from the start. */
export async function loadPlaces(): Promise<void> {
  try {
    const result = await fetchPositions(token());
    if (result.ok) adopt(result.data.positions);
  } catch {
    // Best effort: a long track then starts from the beginning, as before task 49.
  }
}

/**
 * One track's place, afresh — another phone may have moved it. The place the server has, or
 * `undefined` when it has none or could not be asked.
 */
export async function refreshPlace(trackId: number): Promise<number | undefined> {
  try {
    const result = await fetchPositions(token(), [trackId]);
    if (!result.ok) return undefined;
    adopt(result.data.positions);
    return result.data.positions[String(trackId)];
  } catch {
    // Best effort, as above.
    return undefined;
  }
}

/** Sends a place; `true` when the server has it. */
export async function sendPlace(trackId: number, positionS: number): Promise<boolean> {
  try {
    const result = await savePosition(token(), trackId, positionS);
    if (result.ok) places.set(trackId, positionS);
    return result.ok;
  } catch {
    return false;
  }
}
